import React, { useRef, useEffect, useState } from "react";
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import "./DMapStyle.css";
import LayerControl from "./LayerControl";
import { getProjectByName } from '../../../../utils/mergeData';
import bbox from "@turf/bbox";
import { useNavigate, useLocation } from "react-router-dom";
import { getProjectGeoJSON, getProjectCoordinates } from '../../../../utils/mapDataMapping';
import { getMapStyle, getMapStyleUrl, getMapAttribution, getMapStyleOptions } from '../../../../utils/mapStyles';

import { getDeviceLayersForProject, createDeviceLeafletIcon, DEVICE_ICONS_SVG } from '../../../../utils/mapLayerUtils';

const formatFeatureRows = (feature: any) =>
  Object.entries(feature)
    .filter(([key, value]) => key !== "__featureKind" && value !== undefined && value !== null && value !== "")
    .map(([key, value]) => (
      <div key={key} style={{ marginBottom: '8px' }}>
        <strong>{key}:</strong> {String(value)}
      </div>
    ));

const DMapDashBoard: React.FC = () => {
  const selectedProjectStr = localStorage.getItem('selectedProject');
  const selectedProject = selectedProjectStr ? JSON.parse(selectedProjectStr) : null;
  const projectName = selectedProject?.project_name || 'Mangrove';
  
  const DEVICE_LAYERS = getDeviceLayersForProject(projectName);
  const currentProject = getProjectByName(projectName);

  const [activeLayers, setActiveLayers] = useState<string[]>([]);
  const mapContainer = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<L.Map | null>(null);
  const tileLayerRef = useRef<L.TileLayer | null>(null);
  const mapInitialized = useRef<boolean>(false);
  const [selectedFeature, setSelectedFeature] = useState<any>(null);
  const [layers, setLayers] = useState<{ id: string; name: string; opacity: number }[]>([]);
  const [mapStyle, setMapStyle] = useState<string>('satellite');

  const [lng] = useState<number>(getProjectCoordinates(projectName)[0]);
  const [lat] = useState<number>(getProjectCoordinates(projectName)[1]);

  const [tooltip, setTooltip] = useState<{ visible: boolean; content: string; x: number; y: number }>({ visible: false, content: '', x: 0, y: 0 });
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (!mapContainer.current) return;
    if (mapRef.current) { mapRef.current.remove(); mapRef.current = null; }

    try {
      const centerLatLng: [number, number] = [lat, lng];
      mapRef.current = L.map(mapContainer.current as HTMLDivElement, { center: centerLatLng, zoom: 13, zoomControl: true });
      tileLayerRef.current = L.tileLayer(getMapStyleUrl(mapStyle), getMapStyleOptions(mapStyle)).addTo(mapRef.current);

      const geoData = getProjectGeoJSON(projectName);
      const layerId = 'project_boundaries';

      setLayers([
        { id: layerId, name: 'Project Boundaries', opacity: 0.7 },
        ...DEVICE_LAYERS.map(d => ({ id: `device-${d.type.replace(/\s+/g, '-').toLowerCase()}`, name: d.label, opacity: 1 })),
      ]);
      setActiveLayers([layerId, ...DEVICE_LAYERS.map(d => `device-${d.type.replace(/\s+/g, '-').toLowerCase()}`)]);

      const boundaryLayer = L.geoJSON(geoData as any, {
        style: () => ({ fillColor: '#90EE90', fillOpacity: 0.6, color: '#22a34a', weight: 2 }),
        onEachFeature: (feature: any, layer: any) => {
          if (feature.properties) {
            layer.on('click', () => setSelectedFeature(feature.properties));
            layer.on('mouseover', (e: any) => {
              const pt = (mapRef.current as L.Map).latLngToContainerPoint(e.latlng);
              setTooltip({ visible: true, content: Object.entries(feature.properties).map(([k, v]) => `${k}: ${v}`).join('<br/>'), x: pt.x, y: pt.y });
            });
            layer.on('mouseout', () => setTooltip(prev => ({ ...prev, visible: false })));
          }
        }
      }).addTo(mapRef.current);

      const deviceLayerGroups: Record<string, L.LayerGroup> = {};
      DEVICE_LAYERS.forEach(({ type, color }) => { deviceLayerGroups[`device-${type.replace(/\s+/g, '-').toLowerCase()}`] = L.layerGroup(); });

      try {
        const features = (geoData as any).features || [];
        features.forEach((f: any) => {
          if (!f || !f.geometry) return;
          if (f.geometry.type === 'Point') {
            const coords = f.geometry.coordinates;
            const latlng: [number, number] = [coords[1], coords[0]];
            const type = f.properties?.Type || f.properties?.type || '';
            const layerKey = `device-${String(type).replace(/\s+/g, '-').toLowerCase()}`;
            const color = DEVICE_LAYERS.find(dl => dl.type === type)?.color || '#ff5722';
            const marker = L.marker(latlng, { icon: createDeviceLeafletIcon(type, color) });
            if (f.properties) marker.on('click', () => setSelectedFeature(f.properties));
            marker.on('mouseover', (e: any) => { const pt = (mapRef.current as L.Map).latLngToContainerPoint(e.latlng); setTooltip({ visible: true, content: Object.entries(f.properties || {}).map(([k, v]) => `${k}: ${v}`).join('<br/>'), x: pt.x, y: pt.y }); });
            marker.on('mouseout', () => setTooltip(prev => ({ ...prev, visible: false })));
            deviceLayerGroups[layerKey]?.addLayer(marker);
          }
        });
      } catch (e) { console.warn('Failed to populate device layers', e); }

      (mapRef.current as any)._deviceLayerGroups = deviceLayerGroups;
      (mapRef.current as any)._boundaryLayer = boundaryLayer;

      Object.values(deviceLayerGroups).forEach(g => g.addTo(mapRef.current as L.Map));
    } catch (error) {
      console.error('Error initializing map:', error);
    }

    return () => {
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
      }
      mapInitialized.current = false;
      setLayers([]);
      setActiveLayers([]);
    };
  }, [projectName, lng, lat]);

  // Handle map style changes without recreating the map
  useEffect(() => {
    if (!mapRef.current || !tileLayerRef.current) return;

    // Remove old tile layer
    mapRef.current.removeLayer(tileLayerRef.current);

    tileLayerRef.current = L.tileLayer(getMapStyleUrl(mapStyle), getMapStyleOptions(mapStyle)).addTo(mapRef.current);
  }, [mapStyle]);

  const toggleLayerVisibility = (layer: string) => {
    const isActive = activeLayers.includes(layer);
    setActiveLayers(isActive ? activeLayers.filter(id => id !== layer) : [...activeLayers, layer]);
    const map = mapRef.current as any;
    if (!map) return;
    const deviceGroups: Record<string, L.LayerGroup> = map._deviceLayerGroups || {};
    if (deviceGroups[layer]) {
      if (isActive) map.removeLayer(deviceGroups[layer]); else deviceGroups[layer].addTo(map);
      return;
    }
    const boundaryLayer: L.Layer = map._boundaryLayer;
    if (layer === 'project_boundaries' && boundaryLayer) {
      if (isActive) map.removeLayer(boundaryLayer); else (boundaryLayer as any).addTo(map);
    }
  };

  const handleOpacityChange = (layerId: string, newOpacity: number) => {
    setLayers(prev => prev.map(l => l.id === layerId ? { ...l, opacity: newOpacity } : l));
    const map = mapRef.current as any;
    if (!map) return;
    if (layerId === 'project_boundaries') {
      const b = map._boundaryLayer as L.GeoJSON | undefined;
      if (b) b.setStyle({ fillOpacity: newOpacity });
      return;
    }
    const deviceGroups: Record<string, L.LayerGroup> = map._deviceLayerGroups || {};
    const group = deviceGroups[layerId];
    if (group) group.eachLayer((lyr: any) => { if (lyr && typeof lyr.setStyle === 'function') try { lyr.setStyle({ fillOpacity: newOpacity, opacity: newOpacity }); } catch(e){} });
  };

  const handleSiteAnalysis = () => {
    const currentPath = location.pathname;
    const siteName = selectedFeature?.Name;
    if (selectedFeature?.__featureKind === 'device') return;
    if (!siteName) { alert("No site selected."); return; }
    const site = currentProject?.sites?.find((s: any) =>
      s.site_name === siteName ||
      (siteName === "Dwariknagar" && s.site_name === "Drawiknagar") ||
      (siteName === "MER_CR_Demarcation2025_final" && s.site_name === "Mer") ||
      (siteName === "Paglam_CR_Demarcation2025_final" && s.site_name === "Paglam") ||
      (siteName === "SHIVGURI_CR_Demarcation2025_final" && s.site_name === "Shivguri") ||
      (siteName === "Namsing_CR_Demarcation2025_final" && (s.site_name === "Namsing Proxy" || s.site_name === "Namsing Original"))
    );
    if (site) {
      if (currentPath === "/carbonanalysis") { localStorage.setItem("SelectedDropDownCarbonSite", JSON.stringify(site)); navigate('/carbonanalysis-analysis-detail'); }
      else if (currentPath === "/biodiversity") { localStorage.setItem("SelectedDropDownSite", JSON.stringify(site)); navigate('/biodiversity-analysis-detail'); }
      else if (currentPath === "/environmentalreport") { localStorage.setItem("SelectedDropDownEnvSite", JSON.stringify(site)); navigate('/environmentalreport-analysis-detail'); }
    } else {
      alert(`No matching site found for: ${siteName}`);
    }
  };

  const isSelectedDevice = selectedFeature?.__featureKind === 'device';

  return (
    <div className="dmap-dashboard" style={{ position: "relative", height: "65vh", width: "99%" }}>
      {/* Horizontal Legend */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '20px', padding: '8px 15px', backgroundColor: 'white', borderBottom: '1px solid #e2e8f0', flexWrap: 'wrap' }}>
        <span style={{ fontWeight: 600, fontSize: '13px' }}>Devices:</span>
        {DEVICE_LAYERS.map(({ type, color, label }: any) => (
          <div key={type} style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px' }}>
            <div
              style={{ display: 'flex', alignItems: 'center', color }}
              dangerouslySetInnerHTML={{ __html: (DEVICE_ICONS_SVG[type] || '').replace('fill="#ffffff"', `fill="${color}"`) }}
            />
            <span style={{ fontWeight: 500 }}>{label}</span>
          </div>
        ))}
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px' }}>
          <div style={{ width: '14px', height: '14px', backgroundColor: 'rgba(144,238,144,0.6)', border: '1.5px solid #22a34a', borderRadius: '2px' }} />
          <span>Site Boundary</span>
        </div>
      </div>

      <div ref={mapContainer} style={{ position: "absolute", top: 40, bottom: 0, width: "100%" }} />

      {selectedFeature && (
        <div style={{ position: 'absolute', top: '55px', left: '5px', backgroundColor: 'white', padding: '15px', borderRadius: '8px', boxShadow: '0 4px 6px rgba(0,0,0,0.1)', maxWidth: '300px', zIndex: 1001 }}>
          <div style={{ fontSize: '14px' }}>
            {formatFeatureRows(selectedFeature)}
          </div>
          {!isSelectedDevice && (
            <button
              style={{ width: '100%', padding: '10px', marginTop: '15px', backgroundColor: '#005F54', color: 'white', border: 'none', borderRadius: '5px', fontSize: '16px', cursor: 'pointer' }}
              onClick={() => handleSiteAnalysis()}
              onMouseOver={e => (e.currentTarget.style.backgroundColor = '#013731')}
              onMouseOut={e => (e.currentTarget.style.backgroundColor = '#005F54')}
            >Run Analysis</button>
          )}
        </div>
      )}

      <LayerControl layers={layers} activeLayers={activeLayers} toggleLayerVisibility={toggleLayerVisibility} handleOpacityChange={handleOpacityChange} mapStyle={mapStyle} setMapStyle={setMapStyle} />

      {tooltip.visible && (
        <div style={{ position: 'absolute', left: tooltip.x + 10, top: tooltip.y + 10, backgroundColor: 'white', padding: '10px', border: '1px solid #ccc', borderRadius: '4px', boxShadow: '0 2px 6px rgba(0,0,0,0.1)', zIndex: 2, pointerEvents: 'none' }}
          dangerouslySetInnerHTML={{ __html: tooltip.content }} />
      )}
    </div>
  );
};

export default DMapDashBoard;
